/*
 * Editorial product carousel
 *
 * Fetches a Nosto placement as JSON, renders the PLP product card from it via
 * Woolman.NostoCard, and drops the cards into a gridy-slider track.
 *
 * Every carousel on the page shares one recommendation request: Nosto's own
 * section campaigns batch their placements the same way, and a single request
 * keeps the carousels to one call to Nosto.
 *
 * The section hides itself when the placement returns nothing or errors. In
 * the theme editor it shows a notice instead, so a mistyped placement id is
 * visible to merchandisers.
 *
 * The request is queued on nostojs and only settles when Nosto runs it. In
 * the theme editor a timer must not abandon that wait: Nosto finishes
 * starting (and Shopify consent) after the old 8s budget, and the carousel
 * would paint the empty notice and ignore the products that arrive later.
 * Shoppers get the carousel hidden after 8s instead, and a slower answer is
 * dropped so the page does not jump, unless it comes from a consent retry.
 *
 * Every Nosto load() adds a page view. With autoload on, Nosto's own page load
 * has already counted this one, so carousel requests skip page views. With
 * autoload off, the first request records it; a consent retry never does.
 */

(function () {
  const REQUEST_TIMEOUT_MS = 8000;
  const BATCH_WINDOW_MS = 50;

  /* Placements waiting to go out in the next batched request. */
  let pendingPlacements = [];
  let pendingResolvers = [];
  let batchTimer = null;
  let pageViewSent = false;

  function flushBatch() {
    const placements = [...new Set(pendingPlacements)];
    const resolvers = pendingResolvers;
    const skipPageViews = pageViewSent || window.nostoAutoload === true;
    pendingPlacements = [];
    pendingResolvers = [];
    batchTimer = null;
    pageViewSent = true;

    if (!placements.length) return;

    const settle = (value) => resolvers.forEach((resolve) => resolve(value));

    if (typeof window.nostojs !== 'function') {
      settle(null);
      return;
    }

    window.nostojs((api) => {
      const request = api
        .createRecommendationRequest({ includeTagging: true })
        .setElements(placements)
        .setResponseMode('JSON_ORIGINAL');
      const load = skipPageViews ? request.load({ skipPageViews: true }) : request.load();

      load
        .then((response) => settle(response?.recommendations || null))
        .catch((error) => {
          console.error('Editorial carousel: Nosto request failed', error);
          settle(null);
        });
    });
  }

  /**
   * Queue a placement for the next batched recommendation request.
   * @param {string} placement
   * @returns {Promise<Object|null>} The campaign for this placement, or null.
   */
  function requestPlacement(placement) {
    return new Promise((resolve) => {
      pendingPlacements.push(placement);
      pendingResolvers.push((recommendations) => {
        resolve(recommendations ? recommendations[placement] || null : null);
      });
      if (!batchTimer) batchTimer = setTimeout(flushBatch, BATCH_WINDOW_MS);
    });
  }

  class EditorialProductCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.placement = this.dataset.placement;
      this.track = this.querySelector('[data-carousel-track]');
      this.slider = this.querySelector('gridy-slider');
      this.controls = this.querySelector('[data-carousel-controls]');
      this.prevButton = this.controls?.querySelector('[data-direction="prev"]');
      this.nextButton = this.controls?.querySelector('[data-direction="next"]');
      this.designMode = this.dataset.designMode === 'true';

      this.bindArrows();
      this.updateArrowEnds();
      if (this.track) {
        this.onTrackScroll = () => {
          this.updateArrowEnds();
          this.scheduleActiveCards();
        };
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      }

      if (!this.placement || !this.track) return;

      /* Nosto reloads its own placements when the cookie banner is accepted.
         This request is not one of them, so retry once if the first answer
         came back empty while consent was still pending. */
      this.onConsent = () => {
        this.consentChanged = true;
        if (!this.awaitingConsent || this.loaded) return;
        this.awaitingConsent = false;
        clearTimeout(this.consentTimer);
        this.fetchAndRender({ fromConsent: true });
      };
      document.addEventListener('visitorConsentCollected', this.onConsent);

      /* Shoppers shouldn't sit on a skeleton if Nosto never answers. The
         editor keeps the skeleton: the notice is only for a finished request
         that really had no products. Once hidden, only a consent retry may
         show the carousel again (see fetchAndRender). */
      this.giveUpTimer = setTimeout(() => {
        if (this.loaded || this.designMode) return;
        this.hidden = true;
        this.gaveUp = true;
      }, REQUEST_TIMEOUT_MS);

      this.fetchAndRender();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.track && this.onTrackScroll) {
        this.track.removeEventListener('scroll', this.onTrackScroll);
      }
      cancelAnimationFrame(this.activeFrame);
      clearTimeout(this.giveUpTimer);
      clearTimeout(this.consentTimer);
      if (this.onConsent) document.removeEventListener('visitorConsentCollected', this.onConsent);
    }

    consentState() {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.currentVisitorConsent !== 'function') return 'unknown';
      try {
        const { analytics, marketing } = privacy.currentVisitorConsent() || {};
        if (analytics === 'yes' || marketing === 'yes') return 'granted';
        if (analytics === 'no' || marketing === 'no') return 'denied';
        return 'pending';
      } catch (error) {
        return 'unknown';
      }
    }

    async fetchAndRender({ fromConsent = false } = {}) {
      const campaign = await requestPlacement(this.placement);
      if (!this.isConnected || this.loaded) return;
      /* Showing products after the give-up timer hid the carousel would push
         the page around. A consent retry may, as it follows the shopper's own
         click on the cookie banner. Empty answers still go through, so that a
         pending consent can set up that retry. */
      if (this.gaveUp && !fromConsent && campaign?.products?.length) return;
      await this.applyCampaign(campaign);
    }

    async applyCampaign(campaign) {
      const products = campaign?.products || [];

      if (!products.length) {
        const state = this.consentState();
        const decided = state === 'granted' || state === 'denied';
        if (!this.retried && (this.consentChanged || !decided)) {
          this.retried = true;
          if (this.consentChanged || decided) {
            this.fetchAndRender({ fromConsent: Boolean(this.consentChanged) });
            return;
          }
          this.awaitingConsent = true;
          this.consentTimer = setTimeout(() => {
            if (!this.loaded && this.isConnected) this.showEmpty();
          }, REQUEST_TIMEOUT_MS);
          return;
        }
        this.showEmpty();
        return;
      }

      const maxProducts = parseInt(this.dataset.maxProducts, 10) || 8;
      const cards = products
        .slice(0, maxProducts)
        .map((product) => Woolman.NostoCard.fromRecommendation(product))
        .filter(Boolean);

      if (!cards.length) {
        this.showEmpty();
        return;
      }

      clearTimeout(this.giveUpTimer);
      clearTimeout(this.consentTimer);
      this.hidden = false;
      this.awaitingConsent = false;

      const renderer = Woolman.NostoCard.create({
        cardType: this.dataset.cardType || '',
        cardImgAspectRatio: 'portrait_alt',
        cardImgSizingRule: 'fill',
        isFashion: this.dataset.isFashion === 'true',
        // Cards are a fixed width, so the browser never needs a viewport-sized image.
        imageSizes: '(max-width: 767px) 260px, 280px',
        eagerCount: 2,
        // Nosto picks these products, so they need not belong to the offer PLP
        // the carousel sits on. Badges come from each product's own categories.
        useCurrentOfferFallback: false
      });

      const markup = await renderer.renderMarkup(cards);
      if (!this.isConnected || this.loaded) return;
      this.loaded = true;
      this.track.innerHTML = markup;
      this.track.removeAttribute('aria-busy');
      this.track.querySelectorAll('.card--product').forEach((card) => {
        card.setAttribute('role', 'listitem');
      });

      renderer.hydrate(this.track);

      // gridy-slider captured the skeleton as its slides; re-read the real cards.
      this.slider?.update?.();
      this.updateArrowEnds();
      this.track.setAttribute('data-active-card-swipe', '');
      this.updateActiveCards();

      this.setupArrowVisibility();
      this.recordAttribution(campaign);
    }

    /* Hide the whole component, text panel included, when there is nothing to
       show. In the theme editor keep it visible with a notice instead. */
    showEmpty() {
      if (this.loaded || !this.isConnected) return;
      clearTimeout(this.giveUpTimer);
      if (this.designMode) {
        this.track.innerHTML = `<p class="editorial-carousel__notice">Editorial product carousel: the Nosto placement "${this.placement}" returned no products. Check that the placement id is correct and its campaign is live. A personalized campaign also returns nothing for a visitor with no browsing history, unless it has a fallback. In the theme editor that visitor is this browser, unless it has already browsed the store.</p>`;
        this.track.removeAttribute('aria-busy');
        this.controls?.setAttribute('hidden', '');
        return;
      }
      this.hidden = true;
    }

    /* The arrows are the theme's standard gridy-arrows markup, but this
       section scrolls the track itself: gridy-slider tracks position as a
       slide index derived from items-per-row, and with fixed-width cards the
       last scroll positions all clamp to the same offset, which leaves its
       index out of step with the track and strands the prev button. */
    bindArrows() {
      this.controls?.querySelectorAll('[data-gridy-arrow]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          if (button.disabled) return;
          this.step(button.dataset.direction);
        });
      });
    }

    /** Scroll by exactly one card. Stops at either end. */
    step(direction) {
      const cards = this.track.querySelectorAll('.card--product');
      if (cards.length < 2) return;

      const pitch = cards[1].offsetLeft - cards[0].offsetLeft;
      if (!pitch) return;

      const max = this.maxScroll();
      const current = this.track.scrollLeft;
      if (direction === 'next' && current >= max - 1) return;
      if (direction !== 'next' && current <= 1) return;

      const target = direction === 'next' ? current + pitch : current - pitch;
      this.track.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* Previous is disabled at the start, next at the end. Dragging the track
       updates this too, since the listener follows scroll rather than clicks. */
    updateArrowEnds() {
      if (!this.prevButton || !this.nextButton || !this.track) return;
      const max = this.maxScroll();
      const current = this.track.scrollLeft;
      this.prevButton.disabled = current <= 1;
      this.nextButton.disabled = max <= 1 || current >= max - 1;
    }

    /* On touch, image swiping is only live on fully visible cards; a swipe on
       a peeking card moves the track (see component-nosto-card.js and .css).
       Desktop uses hover and never reads this flag. */
    scheduleActiveCards() {
      if (this.activeFrame) return;
      this.activeFrame = requestAnimationFrame(() => {
        this.activeFrame = 0;
        this.updateActiveCards();
      });
    }

    updateActiveCards() {
      if (!this.track) return;
      const bounds = this.track.getBoundingClientRect();
      this.track.querySelectorAll('.card--product').forEach((card) => {
        const rect = card.getBoundingClientRect();
        const visible = rect.left >= bounds.left - 1 && rect.right <= bounds.right + 1;
        const value = visible ? 'true' : 'false';
        if (card.dataset.cardActive !== value) card.dataset.cardActive = value;
      });
    }

    /* Arrows are pointless when every card already fits. Card widths are fixed,
       so the visible count changes with the viewport, not with an items-per-row
       setting. */
    setupArrowVisibility() {
      if (!this.controls || !this.track) return;

      const update = () => {
        this.scheduleActiveCards();
        const overflows = this.track.scrollWidth > this.track.clientWidth + 1;
        this.controls.toggleAttribute('hidden', !overflows);
        this.updateArrowEnds();
      };

      update();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this.track);
      }
    }

    /* Nosto attributes clicks on products inside a rendered campaign, which is
       how a recommendation gets credited when the shopper converts. */
    recordAttribution(campaign) {
      if (typeof window.nostojs !== 'function') return;
      window.nostojs((api) => {
        try {
          api.attributeProductClicksInCampaign(this, campaign);
        } catch (error) {
          console.error('Editorial carousel: attribution failed', error);
        }
      });
    }
  }

  if (!customElements.get('editorial-product-carousel')) {
    customElements.define('editorial-product-carousel', EditorialProductCarousel);
  }
})();
